import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import apiService from '../services/apiService';

const JobApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  const [formData, setFormData] = useState({
    applicationType: 'available',
    selectedJob: '',
    title: 'Mr.',
    firstName: '',
    surname: '',
    phone: '9000000000',
    email: '',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    education: '',
    experience: '',
    employer: '',
    designation: '',
    cvFile: null
  });

  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getJobDetails(id);
      setJobDetails(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAvailableJobs = useCallback(async () => {
    try {
      const jobs = await apiService.getAvailableJobs();
      setAvailableJobs(Array.isArray(jobs) ? jobs : []);
    } catch (err) {
      // Non-blocking: keep silent and allow manual entry
      console.error(err);
      setAvailableJobs([]);
    }
  }, []);

  // Fetch job details and available jobs on component mount
  useEffect(() => {
    fetchJobDetails();
    fetchAvailableJobs();
  }, [fetchJobDetails, fetchAvailableJobs]);

  // availableJobs now comes from API

  const titles = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          cvFile: file,
          cvData: e.target.result,
          cvSize: file.size
        }));
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload a PDF file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // Prepare application data for API
      const applicationData = {
        apply_for_available_jobs: formData.applicationType === 'available',
        selected_job_id: formData.applicationType === 'available' ? id : null,
        title: formData.title,
        first_name: formData.firstName,
        surname: formData.surname,
        phone_number: formData.phone,
        email: formData.email,
        street_address: formData.streetAddress,
        street_address_line2: formData.streetAddress2,
        city: formData.city,
        state_province: formData.state,
        postal_zip_code: formData.postalCode,
        highest_education: formData.education,
        total_experience_years: formData.experience,
        current_last_employer: formData.employer,
        current_last_designation: formData.designation,
        cv_filename: formData.cvFile ? formData.cvFile.name : null,
        cv_data: formData.cvData,
        cv_size: String(formData.cvSize)
      };

      // Submit application to API
      await apiService.submitJobApplication(applicationData);
      
      setSubmitSuccess(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          applicationType: 'available',
          selectedJob: '',
          title: 'Mr.',
          firstName: '',
          surname: '',
          phone: '9000000000',
          email: '',
          streetAddress: '',
          streetAddress2: '',
          city: '',
          state: '',
          postalCode: '',
          education: '',
          experience: '',
          employer: '',
          designation: '',
          cvFile: null,
          cvData: null,
          cvSize: null
        });
        
        // Clear file input
        const fileInput = document.getElementById('cv-upload');
        if (fileInput) fileInput.value = '';
        
        // Navigate back to career page after 3 seconds
        setTimeout(() => {
          navigate('/career');
        }, 3000);
      }, 2000);

    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      applicationType: 'available',
      selectedJob: '',
      title: 'Mr.',
      firstName: '',
      surname: '',
      phone: '90XXXXXXXX',
      email: '',
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      education: '',
      experience: '',
      employer: '',
      designation: '',
      cvFile: null
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading job details...</h3>
          <p className="text-gray-500">Please wait while we fetch the job information.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading job details</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="flex space-x-3 justify-center">
            <button
              onClick={fetchJobDetails}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/career')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Careers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>Job Application - Hospital Build Pvt Ltd</title>
        <meta name="description" content="Apply for positions at Hospital Build Pvt Ltd. Submit your application and CV for career opportunities." />
      </Helmet>

      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {jobDetails ? jobDetails.title : 'Job Application'}
          </h1>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Application submitted successfully! You will be redirected to the careers page shortly.
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {submitError}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Application Type Selection */}
            <div>
              <p className="text-gray-700 mb-4">
                Do you want to apply for available jobs or want to drop your CV for future jobs ?
              </p>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="applicationType"
                    value="available"
                    checked={formData.applicationType === 'available'}
                    onChange={(e) => handleInputChange('applicationType', e.target.value)}
                    className="mr-3 text-blue-600"
                  />
                  <span className="text-gray-700">Yes, I want to apply for available jobs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="applicationType"
                    value="future"
                    checked={formData.applicationType === 'future'}
                    onChange={(e) => handleInputChange('applicationType', e.target.value)}
                    className="mr-3 text-blue-600"
                  />
                  <span className="text-gray-700">No, I want to drop my CV for future jobs</span>
                </label>
              </div>
              
              {formData.applicationType === 'available' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Jobs *
                  </label>
                  <select
                    value={formData.selectedJob}
                    onChange={(e) => handleInputChange('selectedJob', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Please Select</option>
                    {availableJobs.map((job, index) => (
                      <option key={index} value={job.id}>{job.title}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Personal Information */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Personal Information</h2>
              
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <select
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {titles.map((title, index) => (
                      <option key={index} value={title}>{title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => handleInputChange('surname', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">Please enter a registered phone number.</p>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">Please enter a registered email id.</p>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Address Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={formData.streetAddress}
                    onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address Line 2</label>
                  <input
                    type="text"
                    value={formData.streetAddress2}
                    onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal/Zip Code</label>
                    <input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Professional Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Highest Educational Qualification *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., MBA, B.Tech, etc."
                    value={formData.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Years of Experience *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 5"
                    value={formData.experience}
                    onChange={(e) => handleInputChange('experience', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current or Last Employer *
                  </label>
                  <input
                    type="text"
                    placeholder="Company name"
                    value={formData.employer}
                    onChange={(e) => handleInputChange('employer', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current or Last Designation *
                  </label>
                  <input
                    type="text"
                    placeholder="Job title"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* CV Upload */}
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Upload your updated CV here in PDF format
              </h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="cv-upload"
                  required
                />
                <label htmlFor="cv-upload" className="cursor-pointer">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  </div>
                  <p className="text-blue-800 font-medium mb-2">Browse Files</p>
                  <p className="text-gray-600">Drag and drop files here</p>
                </label>
                {formData.cvFile && (
                  <p className="text-sm text-green-600 mt-2">✓ {formData.cvFile.name}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col items-center space-y-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-800 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
              <button
                type="button"
                onClick={handleClearForm}
                disabled={isSubmitting}
                className="text-blue-800 hover:text-blue-800 font-medium disabled:opacity-50"
              >
                Clear Form
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default JobApplication;
